import React, { useState, useEffect } from 'react';
import { X, Mail, Calendar, Activity, Heart, Sparkles, Edit, Ban, Check, Gift } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const UserDetailModal = ({ user, onClose }) => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const { toast } = useToast();
  const [userDetail, setUserDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditPlan, setShowEditPlan] = useState(false);
  const [newPlan, setNewPlan] = useState('');
  const [trialDays, setTrialDays] = useState(7);
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');

  useEffect(() => {
    fetchUserDetail();
  }, [user.id]);

  const fetchUserDetail = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserDetail(data);
        setEditedName(data.name || '');
        setEditedEmail(data.email || '');
      }
    } catch (error) {
      console.error('Error fetching user detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editedName,
          email: editedEmail
        })
      });

      if (response.ok) {
        toast({
          title: "✅ User Updated",
          description: "User details have been updated successfully.",
          duration: 3000,
        });
        setEditMode(false);
        fetchUserDetail();
      } else {
        const error = await response.json();
        toast({
          title: "Update Failed",
          description: error.detail || 'Failed to update user',
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast({
        title: "Update Error",
        description: 'Failed to update user. Please try again.',
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm('Are you sure you want to reset this user\'s password?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.temp_password) {
          toast({
            title: "🔑 Password Reset Successful",
            description: `Temporary Password: ${data.temp_password}\n\nPlease save this and share it securely.`,
            duration: 10000,
          });
        } else {
          toast({
            title: "✅ Password Reset Email Sent",
            description: 'Password reset instructions sent to user.',
            duration: 3000,
          });
        }
      } else {
        const error = await response.json();
        toast({
          title: "Reset Failed",
          description: error.detail || 'Failed to reset password',
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast({
        title: "Reset Error",
        description: 'Failed to reset password. Please try again.',
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleUpdatePlan = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}/plan`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan: newPlan,
          duration_days: newPlan === 'growth_trial' ? trialDays : null
        })
      });

      if (response.ok) {
        toast({
          title: "✅ Plan Updated",
          description: 'User subscription plan updated successfully.',
          duration: 3000,
        });
        setShowEditPlan(false);
        fetchUserDetail();
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast({
        title: "Update Failed",
        description: 'Failed to update plan. Please try again.',
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const handleBanUser = async () => {
    if (!window.confirm('Are you sure you want to ban this user?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${user.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'banned',
          reason: 'Banned by admin'
        })
      });

      if (response.ok) {
        toast({
          title: "✅ User Banned",
          description: 'User has been banned successfully.',
          duration: 3000,
        });
        onClose();
      }
    } catch (error) {
      console.error('Error banning user:', error);
      toast({
        title: "Ban Failed",
        description: 'Failed to ban user. Please try again.',
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  if (loading || !userDetail) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
          <p className="text-center text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-3xl w-full my-8"
           style={{
             background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
             border: '1px solid rgba(255, 182, 193, 0.3)',
           }}>
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-rose-500 to-pink-600 bg-clip-text text-transparent">
            User Details
          </h2>
          <div className="flex items-center gap-2">
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-400 to-indigo-500 text-white rounded-lg hover:shadow-lg transition-all"
              >
                <Edit className="w-4 h-4" />
                Edit Details
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Edit Mode Banner */}
          {editMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-900">Edit Mode Active</p>
                <p className="text-sm text-blue-700">Make your changes and click Save</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveEdit}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditedName(userDetail.name || '');
                    setEditedEmail(userDetail.email || '');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Profile Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Name</label>
              {editMode ? (
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-rose-400"
                />
              ) : (
                <p className="text-lg font-semibold text-gray-900">{userDetail.name || 'N/A'}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Email</label>
              {editMode ? (
                <input
                  type="email"
                  value={editedEmail}
                  onChange={(e) => setEditedEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-rose-400"
                />
              ) : (
                <p className="text-lg text-gray-900">{userDetail.email}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Plan</label>
              <div className="flex items-center gap-2">
                <p className="text-lg font-semibold text-gray-900">
                  {userDetail.subscription_plan || 'free'}
                </p>
                <button
                  onClick={() => setShowEditPlan(true)}
                  className="p-1 hover:bg-rose-100 rounded transition-colors"
                  title="Change Plan"
                >
                  <Edit className="w-4 h-4 text-rose-500" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <p className="text-lg font-semibold text-gray-900">{userDetail.subscription_status}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Joined</label>
              <p className="text-lg text-gray-900">{new Date(userDetail.created_at).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Email Verified</label>
              <p className="text-lg text-gray-900">{userDetail.email_verified ? 'Yes' : 'No'}</p>
            </div>
          </div>

          {/* Password & Security */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-yellow-50 to-orange-50 border border-orange-200">
            <h3 className="font-bold text-gray-900 mb-3">Password & Security</h3>
            <button
              onClick={handleResetPassword}
              className="px-4 py-2 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-lg hover:shadow-lg transition-all"
            >
              🔑 Reset Password
            </button>
            <p className="text-xs text-gray-600 mt-2">
              This will generate a new temporary password and show it to you.
            </p>
          </div>

          {/* Engagement Stats */}
          <div className="p-4 rounded-2xl bg-gradient-to-br from-rose-50 to-pink-50">
            <h3 className="font-bold text-gray-900 mb-4">Engagement Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="p-3 bg-white rounded-xl mb-2">
                  <Heart className="w-6 h-6 mx-auto text-rose-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{userDetail.engagement.avg_love_score}</p>
                <p className="text-xs text-gray-600">Love Score</p>
              </div>
              <div className="text-center">
                <div className="p-3 bg-white rounded-xl mb-2">
                  <Calendar className="w-6 h-6 mx-auto text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{userDetail.engagement.journal_count}</p>
                <p className="text-xs text-gray-600">Journals</p>
              </div>
              <div className="text-center">
                <div className="p-3 bg-white rounded-xl mb-2">
                  <Sparkles className="w-6 h-6 mx-auto text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{userDetail.engagement.affirmations_count}</p>
                <p className="text-xs text-gray-600">Affirmations</p>
              </div>
              <div className="text-center">
                <div className="p-3 bg-white rounded-xl mb-2">
                  <Activity className="w-6 h-6 mx-auto text-green-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{userDetail.engagement.current_streak}</p>
                <p className="text-xs text-gray-600">Day Streak</p>
              </div>
            </div>
          </div>

          {/* Change Plan Modal */}
          {showEditPlan && (
            <div className="p-4 rounded-2xl bg-blue-50 border border-blue-200">
              <h4 className="font-bold text-gray-900 mb-3">Change User Plan</h4>
              <div className="space-y-3">
                <select
                  value={newPlan}
                  onChange={(e) => setNewPlan(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-300"
                >
                  <option value="">Select Plan</option>
                  <option value="free">Free</option>
                  <option value="growth_trial">Trial</option>
                  <option value="growth">Paid (Growth)</option>
                </select>

                {newPlan === 'growth_trial' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trial Duration (Days)</label>
                    <input
                      type="number"
                      value={trialDays}
                      onChange={(e) => setTrialDays(parseInt(e.target.value))}
                      min="1"
                      max="90"
                      className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-300"
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleUpdatePlan}
                    disabled={!newPlan}
                    className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-400 to-indigo-500 text-white font-medium disabled:opacity-50"
                  >
                    Update Plan
                  </button>
                  <button
                    onClick={() => setShowEditPlan(false)}
                    className="px-4 py-2 rounded-xl bg-gray-200 text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleBanUser}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors"
            >
              <Ban className="w-4 h-4" />
              Ban User
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailModal;